import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function ApprovalsPanel() {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');

  // Vérifier si l'utilisateur peut approuver
  const canApprove = ['superviseur', 'manager'].includes(user?.role);

  useEffect(() => {
    if (canApprove) {
      fetchApprovals();
    }
  }, [canApprove]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/workflow/approvals');
      if (response.data.success) {
        setApprovals(response.data.approvals);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des approbations:', error);
      
      // Ne pas afficher d'erreur si c'est juste un problème de permissions
      if (error.response?.status === 403) {
        console.log('Accès refusé aux approbations - permissions insuffisantes');
      } else if (error.response?.status !== 401) {
        // Afficher l'erreur seulement si ce n'est pas un problème d'auth
        console.warn('Erreur lors du chargement des demandes d\'approbation:', error.response?.data?.error || error.message);
      }
      
      // Initialiser une liste vide en cas d'erreur
      setApprovals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (approvalId, status) => {
    if (!window.confirm(`Confirmer ${status === 'approuve' ? 'l\'approbation' : 'le rejet'} de cette demande ?`)) {
      return;
    }

    try {
      const response = await axios.post(`/api/workflow/approve/${approvalId}`, {
        statut: status,
        commentaire: approvalComment.trim() || null
      });

      if (response.data.success) {
        alert(`Demande ${status === 'approuve' ? 'approuvée' : 'rejetée'} avec succès !`);
        setSelectedApproval(null);
        setApprovalComment('');
        fetchApprovals(); // Recharger la liste
      }
    } catch (error) {
      console.error('Erreur lors de l\'approbation:', error);
      const errorMsg = error.response?.data?.error || 'Erreur lors du traitement de la demande';
      alert(`Erreur: ${errorMsg}`);
    }
  };

  const getPriorityColor = (priorite) => {
    switch (priorite) {
      case 'critique': return '#dc3545';
      case 'haute': return '#fd7e14';
      case 'normale': return '#ffc107';
      case 'faible': return '#28a745';
      default: return '#6c757d';
    }
  };

  // 🔧 Décoder les entités HTML (fix sanitisation serveur)
  const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text
      .replace(/&#x2F;/g, '/')
      .replace(/&#x3A;/g, ':')
      .replace(/&quot;/g, '"')
      .replace(/&#x27;/g, "'")
      .replace(/&amp;/g, '&');
  };

  const formatDate = (dateString) => {
    const decodedDate = decodeHtmlEntities(dateString);
    return new Date(decodedDate).toLocaleString('fr-FR');
  };

  const formatCost = (cost) => {
    if (!cost) return 'Non estimé';
    return `${parseInt(cost).toLocaleString('fr-FR')}€`;
  };

  if (!canApprove) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        background: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        color: '#6c757d'
      }}>
        <p>Cette section est réservée aux superviseurs et managers.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>🔄 Chargement des demandes d'approbation...</div>
      </div>
    );
  }

  return (
    <div style={{
      border: '2px solid #9cb85c',
      borderRadius: '8px',
      padding: '20px',
      background: '#f8f9fa'
    }}>
      <h3 style={{ margin: '0 0 20px 0', color: '#9cb85c' }}>
        ✅ Demandes d'Approbation ({approvals.length})
      </h3>

      {approvals.length === 0 ? (
        <div style={{
          textAlign: 'center',
          color: '#6c757d',
          padding: '20px',
          fontStyle: 'italic'
        }}>
          Aucune demande d'approbation en attente. ✨
        </div>
      ) : (
        <div>
          {approvals.map((approval) => (
            <div
              key={approval.approval_id}
              style={{
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px',
                background: 'white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {/* En-tête de la demande */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <div>
                  <strong style={{ fontSize: '16px' }}>
                    Formulaire #{approval.formulaire_id}
                  </strong>
                  <span style={{
                    marginLeft: '10px',
                    background: getPriorityColor(approval.priorite),
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 'bold'
                  }}>
                    {approval.priorite?.toUpperCase()}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  Demandé le {formatDate(approval.date_demande)}
                </div>
              </div>

              {/* Détails du problème */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                  📍 {approval.equipement_zone_ligne || 'Équipement non spécifié'}
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <strong>Problème:</strong> {approval.probleme_initial || 'Non spécifié'}
                </div>
                <div style={{ fontSize: '14px', color: '#6c757d' }}>
                  <strong>Créé par:</strong> {approval.created_by_prenom} {approval.created_by_nom}
                  {approval.cout_estime && (
                    <span style={{ marginLeft: '15px' }}>
                      <strong>Coût estimé:</strong> {formatCost(approval.cout_estime)}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions d'approbation */}
              <div style={{
                borderTop: '1px solid #dee2e6',
                paddingTop: '10px',
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-end'
              }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}>
                    Commentaire (optionnel):
                  </label>
                  <textarea
                    value={selectedApproval === approval.approval_id ? approvalComment : ''}
                    onChange={(e) => {
                      setSelectedApproval(approval.approval_id);
                      setApprovalComment(e.target.value);
                    }}
                    placeholder="Commentaire sur votre décision..."
                    style={{
                      width: '100%',
                      height: '50px',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => handleApproval(approval.approval_id, 'approuve')}
                    style={{
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    ✅ Approuver
                  </button>
                  <button
                    onClick={() => handleApproval(approval.approval_id, 'rejete')}
                    style={{
                      background: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    ❌ Rejeter
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bouton de rafraîchissement */}
      <div style={{ textAlign: 'center', marginTop: '15px' }}>
        <button
          onClick={fetchApprovals}
          style={{
            background: '#9cb85c',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          🔄 Actualiser
        </button>
      </div>
    </div>
  );
}

export default ApprovalsPanel;
